import { SagaIterator } from '@redux-saga/core';
import { call, put, takeLeading, takeLatest, all } from 'redux-saga/effects';

import api from '~/store/api';
import { creators as notificationCreators } from '~/store/notification';

import { creators } from './actions';
import { formatSavedTrialFilters, formatTimeLocations, formatTrialArmSites } from './helpers';
import { GetTrialFiltersAndMatchingQueue } from '../api/types/timeScreeningStore';

function* getSavedTrialFilters({ trialId }: GetTrialFiltersAndMatchingQueue): SagaIterator {
  try {
    const trialFilters = yield call(api.trials.getSavedTrialFilters, trialId);
    const filters = formatSavedTrialFilters(trialFilters);
    yield put(creators.setTrialSavedFilters({ ...filters, trialId }));
  } catch (error) {
    yield put(notificationCreators.showErrorMessage(`Getting saved trial filters failed - ${error}`));
  }
}

function* getTrials(): SagaIterator {
  try {
    const trials = yield call(api.trials.getForTimeScreeningDropdown);
    yield put(creators.setTrialOptions({ trials }));
  } catch (error) {
    yield put(creators.setTrialOptions({ trials: [] }));
    yield put(notificationCreators.showErrorMessage(`Getting screening trials failed - ${error}`));
  }
}

function* getTrialCohorts({ trialId }: GetTrialFiltersAndMatchingQueue): SagaIterator {
  try {
    const { data: cohorts } = yield call(api.arms.getForTimeScreeningDropdown, trialId);
    yield put(creators.setTrialOptions({ cohorts }));
    return cohorts;
  } catch (error) {
    yield put(creators.setTrialOptions({ cohorts: [] }));
    yield put(notificationCreators.showErrorMessage(`Getting trial cohorts failed - ${error}`));
  }
}

function* getTrialArmSites({ trialId, armId }: GetTrialFiltersAndMatchingQueue): SagaIterator {
  try {
    const { timeSites } = yield call(api.timeSites.getTrialArmSites, trialId, armId);
    const sites = formatTrialArmSites(timeSites);
    yield put(creators.setTrialOptions({ sites }));
  } catch (error) {
    yield put(notificationCreators.showErrorMessage(`Getting trial cohort sites failed - ${error}`));
  }
}

function* fetchTimeInstitutions(): SagaIterator {
  try {
    const rows = yield call(api.timeScreening.getTimeInstitutions);
    yield put(creators.setTrialOptions({ locations: formatTimeLocations(rows) }));
  } catch (error) {
    yield put(creators.setTrialOptions({ locations: [] }));
    yield put(notificationCreators.showErrorMessage(`Getting time institutions failed - ${error}`));
  }
}

function* getTrialFilterOptions(action: GetTrialFiltersAndMatchingQueue): SagaIterator {
  yield call(getSavedTrialFilters, action);
  yield all([call(getTrialArmSites, action), call(getTrialCohorts, action), call(fetchTimeInstitutions)]);
}

export default [
  takeLeading('GET_SCREENING_TRIALS', getTrials),
  takeLatest('GET_TRIAL_FILTER_OPTIONS', getTrialFilterOptions),
  takeLatest('GET_TIME_INSTITUTIONS', fetchTimeInstitutions),
];
